import React, { useState, useEffect, useCallback } from 'react';
import { TestFlightTester, TestFlightStats } from '../../types';
import TestFlightStatsGrid from './TestFlightStatsGrid';
import TestFlightFilters from './TestFlightFilters';
import TestFlightBulkActions from './TestFlightBulkActions';
import TestFlightTestersTable from './TestFlightTestersTable';

interface TestFlightTestersProps {
  onRefresh: () => void;
}

const TestFlightTesters: React.FC<TestFlightTestersProps> = ({ onRefresh }) => {
  const [testers, setTesters] = useState<TestFlightTester[]>([]);
  const [filteredTesters, setFilteredTesters] = useState<TestFlightTester[]>([]);
  const [stats, setStats] = useState<TestFlightStats | null>(null);
  const [selectedTesters, setSelectedTesters] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('email');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    loadTestFlightData();
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...testers];

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(tester => {
        const testerStatus = tester.attributes.betaTesterState || tester.attributes.state || 'UNKNOWN';
        return testerStatus === statusFilter;
      });
    }

    // Apply group filter
    if (groupFilter) {
      filtered = filtered.filter(tester => 
        tester.groupName === groupFilter
      );
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(tester => {
        const name = [tester.attributes.firstName, tester.attributes.lastName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return (
          tester.attributes.email.toLowerCase().includes(searchLower) ||
          name.includes(searchLower)
        );
      });
    }

    setFilteredTesters(filtered);
  }, [testers, statusFilter, groupFilter, searchTerm]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const loadTestFlightData = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Load testers and stats in parallel
      const [testersResponse, statsResponse] = await Promise.all([
        fetch('/api/testflight/testers'),
        fetch('/api/testflight/stats')
      ]);

      if (!testersResponse.ok) throw new Error('Failed to fetch testers');
      if (!statsResponse.ok) throw new Error('Failed to fetch stats');

      const testersData = await testersResponse.json();
      const statsData = await statsResponse.json();

      setTesters(testersData.testers || []);
      setStats(statsData.stats);
      
      // Console log the first tester to see the format
      if (testersData.testers && testersData.testers.length > 0) {
        console.log('First TestFlight tester format:', testersData.testers[0]);
      }
      
    } catch (err) {
      console.error('Error loading TestFlight data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load TestFlight data');
    } finally {
      setIsLoading(false);
    }
  };


  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
  };

  const handleGroupFilter = (group: string) => {
    setGroupFilter(group);
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
  };

  const handleSort = (column: string, order: 'asc' | 'desc') => {
    setSortBy(column);
    setSortOrder(order);
    
    const sorted = [...filteredTesters].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (column) {
        case 'email':
          aVal = a.attributes.email;
          bVal = b.attributes.email;
          break;
        case 'name':
          aVal = [a.attributes.firstName, a.attributes.lastName].filter(Boolean).join(' ');
          bVal = [b.attributes.firstName, b.attributes.lastName].filter(Boolean).join(' ');
          break;
        case 'status':
          aVal = a.attributes.betaTesterState || a.attributes.state || 'UNKNOWN';
          bVal = b.attributes.betaTesterState || b.attributes.state || 'UNKNOWN';
          break;
        case 'group':
          aVal = a.groupName;
          bVal = b.groupName;
          break;
        case 'inviteType':
          aVal = a.attributes.inviteType || '';
          bVal = b.attributes.inviteType || '';
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredTesters(sorted);
  };

  const handleSelectionChange = (testerId: string, selected: boolean) => {
    if (selected) {
      setSelectedTesters(prev => [...prev, testerId]);
    } else {
      setSelectedTesters(prev => prev.filter(id => id !== testerId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedTesters(filteredTesters.map(tester => tester.id));
    } else {
      setSelectedTesters([]);
    }
  };

  const handleSelectByStatus = (status: string) => {
    const testersWithStatus = filteredTesters
      .filter(tester => {
        const testerStatus = tester.attributes.betaTesterState || tester.attributes.state || 'UNKNOWN';
        return testerStatus === status;
      })
      .map(tester => tester.id);
    setSelectedTesters(testersWithStatus);
  };

  const handleClearSelection = () => {
    setSelectedTesters([]);
  };

  const handleExportSelected = () => {
    if (selectedTesters.length === 0) {
      alert('Please select testers to export');
      return;
    }

    const selectedTestersData = testers.filter(tester => 
      selectedTesters.includes(tester.id)
    );

    // Create CSV content - only email and name like the original
    const csvContent = selectedTestersData.map(tester => {
      const name = [tester.attributes.firstName, tester.attributes.lastName].filter(Boolean).join(' ');
      return `"${tester.attributes.email}","${name}"`;
    }).join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `testflight-testers-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const testConnection = async () => {
    try {
      const response = await fetch('/api/testflight/test-connection');
      if (response.ok) {
        alert('TestFlight connection successful!');
      } else {
        const error = await response.json();
        alert('TestFlight connection failed: ' + (error.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Connection test error:', err);
      alert('Connection test failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const getUniqueGroups = () => {
    const groupSet = new Set(testers.map(tester => tester.groupName));
    const groups = Array.from(groupSet);
    return groups.sort();
  };

  if (error) {
    return (
      <div className="content-section">
        <div className="card">
          <div className="error-message">
            <h3>Error Loading TestFlight Data</h3>
            <p>{error}</p>
            <button onClick={loadTestFlightData} className="btn btn-primary">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="testflight-testers">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <span>👥</span>
            TestFlight Testers
          </h3>
          <p className="card-description">View and manage TestFlight beta testers and their status</p>
          <div className="flex gap-4 items-center mt-4">
            <button 
              className="btn btn-primary" 
              onClick={loadTestFlightData}
              disabled={isLoading}
            >
              <span>🔄</span>
              {isLoading ? 'Loading...' : 'Refresh Testers'}
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={testConnection}
              disabled={isLoading}
            >
              <span>🔗</span>
              Test Connection
            </button>
          </div>
        </div>

        {stats && !isLoading && (
          <TestFlightStatsGrid 
            stats={stats} 
            selectedGroup={groupFilter}
          />
        )}

        <TestFlightFilters
          onStatusFilter={handleStatusFilter}
          onGroupFilter={handleGroupFilter}
          onSearch={handleSearch}
          groups={getUniqueGroups()}
          filteredCount={filteredTesters.length}
          totalCount={testers.length}
        />

        <TestFlightBulkActions
          selectedCount={selectedTesters.length}
          onSelectAllVisible={() => setSelectedTesters(filteredTesters.map(t => t.id))}
          onSelectByStatus={handleSelectByStatus}
          onClearSelection={handleClearSelection}
          onExportSelected={handleExportSelected}
        />

        <TestFlightTestersTable
          testers={filteredTesters}
          selectedTesters={selectedTesters}
          onSelectionChange={handleSelectionChange}
          onSelectAll={handleSelectAll}
          onSort={handleSort}
          sortBy={sortBy}
          sortOrder={sortOrder}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default TestFlightTesters;
